import { Suspense, useState } from 'react';
import { MissingResourceError } from '#api/errors.ts';
import { useSuspenseTagQuery } from '#api/tags.queries.ts';
import { DeleteIcon } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useReturnNavigation } from '#hooks/useAppLocation.ts';
import { useZodParams } from '#hooks/useZodParams.ts';
import { tagNameSchema } from '#schemas/tag/name.ts';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { fromArray, Titled } from '#util/title.ts';
import { DeleteTagDialog } from '../delete/DeleteTagDialog.tsx';
import { mapTagToForm } from '../form/mapTagToForm.ts';
import { TagForm } from '../form/TagForm.tsx';

export function EditTagPage() {
  const { tagName } = useZodParams({
    tagName: tagNameSchema,
  });
  const returnTo = useReturnNavigation('/tags');
  const [tagBeingDeleted, setTagBeingDeleted] = useState<boolean>(false);

  const {
    data: { data: tag },
    error,
    isFetching,
  } = useSuspenseTagQuery(tagName);

  // Check if this has been deleted (e.g. in a different tab)
  if (error && !isFetching && error instanceof MissingResourceError) {
    throw error;
  }

  return (
    <Titled title={(title) => fromArray([tag.name, title])}>
      <MainLayout
        header={
          <PageHeader
            title={
              <>
                <Link to="/tags" className="a">
                  Tags
                </Link>
                {' / '}
                {tag.name}
              </>
            }
            subtitle={
              <span className="mb-8">
                Assigned to{' '}
                {tag.hostCount > 0 ?
                  <Link className="a" to={`/hosts?filter.tags=${encodeURIComponent(tag.name)}`}>
                    {tag.hostCount} {tag.hostCount === 1 ? 'host' : 'hosts'}
                  </Link>
                : '0 hosts'}
              </span>
            }
          >
            <Tooltip
              content="Delete"
              render={
                <Button
                  size="small"
                  variant="icon"
                  onClick={() => {
                    setTagBeingDeleted(true);
                  }}
                />
              }
            >
              <DeleteIcon aria-hidden="true" />
              <span className="visually-hidden">Delete</span>
            </Tooltip>
          </PageHeader>
        }
      >
        <Suspense fallback={<LoadingSpinner />}>
          <TagForm
            tagName={tagName}
            hostCount={tag.hostCount}
            onSuccess={() => {
              returnTo();
            }}
            defaultValues={mapTagToForm(tag)}
          />
        </Suspense>
        {tagBeingDeleted ?
          <DeleteTagDialog
            tagName={tagName}
            onCancel={() => setTagBeingDeleted(false)}
            onDelete={() => {
              returnTo();
            }}
          />
        : null}
      </MainLayout>
    </Titled>
  );
}
